import React, { createContext, useContext } from 'react';
import { AuthProvider } from '../contexts/AuthContext';
import { LearningConsentProvider } from '../contexts/LearningConsentContext';
import {
  JyotishaLanguageProvider,
  useJyotishaLanguage,
} from '../components/jyotisha/JyotishaLanguageContext';
import { FeatureVisibilityProvider } from '../contexts/FeatureVisibilityContext';
import { ClapActivityToastProvider } from '../contexts/ClapActivityToastContext';
import { ParashariPreferencesProvider } from '../hooks/useParashariPreferencesSync';
import { usePlatformTheme } from '../hooks/usePlatformTheme';
import 'schools/parashari/parashari-styles.css';

const ParashariAppContext = createContext(false);

export function useInParashariApp(): boolean {
  return useContext(ParashariAppContext);
}

/** Composite font stack so Hindi/English toggles do not swap font-family on the whole tree. */
function AppLangSurface({ children }: { children: React.ReactNode }) {
  const { showEnglish } = useJyotishaLanguage();
  return (
    <div
      className="app-lang-surface min-h-full"
      data-show-english={showEnglish ? 'true' : 'false'}
      lang={showEnglish ? 'en' : 'hi'}
    >
      {children}
    </div>
  );
}

function ParashariAppProvidersInner({ children }: { children: React.ReactNode }) {
  const { isDark } = usePlatformTheme();

  return (
    <ParashariPreferencesProvider>
      <ClapActivityToastProvider isDark={isDark}>
        <AppLangSurface>{children}</AppLangSurface>
      </ClapActivityToastProvider>
    </ParashariPreferencesProvider>
  );
}

/**
 * Shared Parashari provider stack for authenticated AppLayout routes.
 * Includes global ClapActivityToastProvider (cross-page clap broadcast toasts).
 */
export function ParashariAppProviders({ children }: { children: React.ReactNode }) {
  return (
    <ParashariAppContext.Provider value={true}>
      <AuthProvider>
        <LearningConsentProvider>
          <JyotishaLanguageProvider persist={false} defaultShowEnglish>
            <FeatureVisibilityProvider>
              <ParashariAppProvidersInner>{children}</ParashariAppProvidersInner>
            </FeatureVisibilityProvider>
          </JyotishaLanguageProvider>
        </LearningConsentProvider>
      </AuthProvider>
    </ParashariAppContext.Provider>
  );
}
